import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment-timezone';
import {
  MyErrorStateMatcher,
  ValidatorConfig,
} from '../../../../../../../common/field-validation/my-error-state-matcher';
import { UiValidator } from '../../../../../../../common/field-validation/ui-validator.dto';
import { ImageUtils } from '../../../../../../../common/utils/image-utils';
import { FieldConfigDTO, LookupChange, LookupEntityConf } from '../../../../../../../system/system-dtos';
import { SystemService } from '../../../../../../../system/system.service';
import { isDefined } from '../../../../../../utils/utils';
import { BlastV2Service } from '../../../../../../views/main-table-v2/service/blast-v2.service';
import { DataTypeComponent } from '../../../../data-type-component';
import { MoneyService } from '../../../money/money.service';
import { LookupModalComponent, LookupModalInitialData } from '../../lookup-modal/lookup-modal.component';
import { GameSearchModalComponent } from '../../game-search-modal/game-search-modal.component';
import { GAME_SEARCH_DIALOG_CONFIG } from '../../game-search-modal/game-search-modal.config';
import { LookupService } from '../../lookup.service';
import { filter } from 'rxjs/operators';

const GAME_LOOKUP_CODES = new Set(['game_instance']);

@Component({
  selector: 'app-lookup-from-field',
  templateUrl: './lookup-from-field.component.html',
  styleUrls: ['./lookup-from-field.component.scss'],
  standalone: false,
})
export class LookupFormFieldComponent extends DataTypeComponent implements OnInit {
  @Input() data: any;
  @Input() editMode: boolean;
  @Input() fieldConfig: FieldConfigDTO;
  @Input() formGroup: UntypedFormGroup;

  @Input() lookup: string;
  @Input() fieldName: any;
  rowData: any;

  label = '';
  icon = '';
  showIcon = false;

  public timezone: string;

  lookupCode = '';
  imageSrc: string;
  editReadOnly: boolean = false;

  private lookupConfig: LookupEntityConf;

  matcher = new MyErrorStateMatcher();
  private validatorsSet = false;
  public externalUrl: string;

  constructor(
    private lookupService: LookupService,
    private moneyService: MoneyService,
    private systemService: SystemService,
    private dialog: MatDialog,
    private ref: ChangeDetectorRef,
    private blastService: BlastV2Service
  ) {
    super();
    this.systemService.systemTimeZoneSubject.subscribe((timezone: string) => {
      this.timezone = timezone;
    });
  }

  ngOnInit() {
    this.lookupCode = this.getLookupcode();
    this.lookupConfig = this.lookupService.getConfByLookupCode(this.getLookupcode());
    if (this.formGroup !== undefined) {
      const data = this.formGroup.getRawValue();
      this.editReadOnly = this.formGroup.get(this.getFieldName()).disabled;
      if (!this.data || typeof this.data === 'string') {
        const id = this.data ? this.data : this.formGroup.get(this.getFieldName()).value;
        if (!id) {
          return;
        }
        const cacheData = this.blastService.getCollection(this.getLookupcode());
        this.data = cacheData ? cacheData.find((d) => d[this.fieldConfig.idFieldName] == id) : this.data;
      }
    }
    if (this.data && this.lookupCode === 'image') {
      this.imageSrc = ImageUtils.relocateImage(this.data);
    }
    this.label = this.extractLabel(this.data);
    this.icon = this.extractImage(this.data);
    this.showIcon = this.shouldShowImageOnly(this.data);

    this.externalUrl = this.getExternalUrl();
    this.initialise();
  }

  // do not remove - called out of scope by extractLabel
  private formatAsMoney(value: any): string {
    return this.moneyService.getTicketPrice(value);
  }

  // do not remove - called out of scope by extractLabel
  private formatAsDate(value: any, fieldConfig: FieldConfigDTO): string {
    if (isDefined(fieldConfig) && isDefined(fieldConfig.dateFormat)) {
      return moment(value).tz(this.timezone).format(fieldConfig.dateFormat);
    } else {
      return moment(value).tz(this.timezone).format('DD-MMM');
    }
  }

  private extractLabel(data: any) {
    if (isDefined(data) && isDefined(this.lookupConfig) && isDefined(this.lookupConfig.extractLabel)) {
      try {
        const func = new Function('self', 'data', this.lookupConfig.extractLabel);
        const val = func(this, data);
        return val;
      } catch (err) {
        console.error('Failed to extract label', this.lookupConfig.extractLabel, data, err);
        return '';
      }
    }
    return undefined;
  }

  private shouldShowImageOnly(data: any): boolean {
    if (
      isDefined(data) &&
      isDefined(this.lookupConfig) &&
      isDefined(this.lookupConfig.showImage) &&
      this.lookupConfig.showImage
    ) {
      return true;
    }
    return false;
  }

  private extractImage(data: any) {
    if (isDefined(data) && isDefined(this.lookupConfig) && isDefined(this.lookupConfig.extractImage)) {
      const func = new Function('self', 'data', this.lookupConfig.extractImage);
      const val = func(this, data);
      return val.replace('./assets/images/', 'https://storage.googleapis.com/kickthe/assets/images/');
    }
    return undefined;
  }

  private getLookupcode() {
    return !!this.lookup ? this.lookup : this.fieldConfig.lookup;
  }

  openModal() {
    const resolveUrl = this.fieldConfig?.selectUrl || this.lookupConfig?.defaultSelectUrl;
    const matDialogRef = this.isGameLookup()
      ? this.dialog.open(GameSearchModalComponent, {
          ...GAME_SEARCH_DIALOG_CONFIG,
          data: { resolveUrl },
        })
      : this.dialog.open(LookupModalComponent, {
          minWidth: '800px',
          data: {
            lookup: this.getLookupcode(),
            fieldConfig: this.fieldConfig,
            context: this.formGroup.getRawValue(),
            entity: this.rowData,
          } as LookupModalInitialData,
        });

    matDialogRef
      .afterClosed()
      .pipe(filter((ret) => !!ret))
      .subscribe((ret) => {
        const fieldName = this.getFieldName();
        let val = ret;
        if (isDefined(this.lookupConfig.retValue)) {
          try {
            const func = new Function('self', 'data', this.lookupConfig.retValue);
            val = func(this, ret);
          } catch (err) {
            console.log('Error extracting return value, using lookup', err, ret, this.lookupConfig);
          }
        }
        this.syncGameLinkFields(val);
        this.formGroup.get(fieldName).setValue(val);

        this.label = this.extractLabel(ret);
        this.icon = this.extractImage(ret);
        this.showIcon = this.shouldShowImageOnly(ret);

        this.lookupService.getLookupChangeSubject().next({
          field: fieldName,
          value: this.formGroup.get(fieldName).value,
        } as LookupChange);
        if (this.lookupCode === 'image') {
          this.imageSrc =
            ImageUtils.getCloudURL() + this.formGroup.get(fieldName).value.replace('./assets/', '/assets/');
        }

        this.ref.markForCheck();
      });
  }

  private syncGameLinkFields(gameInstance: any): void {
    if (!gameInstance || !this.formGroup) {
      return;
    }

    const logoUrl = this.toRelativeAssetPath(gameInstance.logoUrl || gameInstance.imageUrl);
    if (logoUrl && this.formGroup.get('image')) {
      this.formGroup.get('image').setValue(logoUrl);
      if (this.rowData) {
        this.rowData.image = logoUrl;
      }
    }

    if (gameInstance.gameId != null && this.formGroup.get('gameId')) {
      this.formGroup.get('gameId').setValue(gameInstance.gameId);
    }
    if (gameInstance.cohortId != null && this.formGroup.get('cohortId')) {
      this.formGroup.get('cohortId').setValue(gameInstance.cohortId);
    }
    if (gameInstance.id != null && this.formGroup.get('gameInstanceId')) {
      this.formGroup.get('gameInstanceId').setValue(gameInstance.id);
    }

    if (this.rowData) {
      this.rowData.gameInstance = gameInstance;
    }
  }

  private toRelativeAssetPath(url: string): string {
    if (!url) {
      return url;
    }
    if (url.startsWith('./assets/')) {
      return url;
    }
    const marker = '/assets/';
    const idx = url.indexOf(marker);
    if (idx >= 0) {
      return '.' + url.substring(idx);
    }
    return url;
  }

  private isGameLookup(): boolean {
    const lookupCode = this.getLookupcode();
    if (GAME_LOOKUP_CODES.has(lookupCode)) {
      return true;
    }

    const selectUrl = this.fieldConfig?.selectUrl || this.lookupConfig?.defaultSelectUrl || '';
    return selectUrl.includes('/game/casinofreespinlookup') || selectUrl.includes('/game/casinolookup');
  }

  private getFieldName() {
    return this.fieldName ? this.fieldName : this.fieldConfig.name;
  }

  condition(v: UiValidator, ngModel: NgModel) {
    if (!this.validatorsSet) {
      this.validatorsSet = true;
      ngModel.control.setValidators(MyErrorStateMatcher.convertToAngularValidators([this.fieldConfig]));
    }

    return this.getValidatorConfig(v).condition(ngModel.control);
  }

  message(v: UiValidator) {
    return this.getValidatorConfig(v).message(v);
  }

  private getValidatorConfig(v: UiValidator): ValidatorConfig {
    return MyErrorStateMatcher.messageMap[v.type];
  }

  public getExternalUrl(): string {
    const label = this.label;
    const data = this.data;

    if (!this.lookupConfig || !this.lookupConfig.externalUrl || !label) {
      return null;
    }
    const ret = eval(this.lookupConfig.externalUrl);
    return ret;
  }

  public clearField() {
    this.label = null;
    if (this.fieldConfig.clearableImgLookup && this.lookupCode === 'image') {
      this.formGroup.get(this.getFieldName()).setValue('');
      this.imageSrc = '';
    } else {
      this.formGroup.get(this.getFieldName()).setValue(null);
      this.imageSrc = null;
    }
  }

  setRowData(entity: any) {
    this.rowData = entity;
  }
}
